import { FC, useEffect, useMemo, useState } from 'react'
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline'
import {
  importShipmentsFromCSV,
  importShipmentsFromXLSX,
} from '../../utils/imports'
import {
  FormFile,
  FormText,
  MainPage,
  PageTitle,
  PrimaryButton,
  SecondaryButton,
  HorizontalTab,
  LinkTextIcon,
  ModalLoading,
  ConfirmModalStatus,
  Modal,
} from '../../components'
import { ImportInterface } from '../../interfaces/Import/importInterfase'
import {
  BusinessType,
  BusinessUnitInterface,
  CCWIntegracionV2,
  DeliveryEnum,
} from '../../interfaces'
import { useAppSelector } from '../../store/hooks'
import { ImportsMasiveTable } from '../../components/Import/ImportsMasiveTable'
import { getShipmentsExists } from '../../services'
import {
  exportFormatoShipmentXLSX,
  exportFormatoShipmentXLSX_V2,
} from '../../utils/exports'
import {
  searchManifiestInternational,
  searchManifiestInternationalExcel,
} from '../../services/ImportServices'
import { ImportMapperV2Dto } from '../../utils/mappers'

let CONSIGNEE_TABS = [
  {
    name: 'Archivo',
    icon: <DocumentTextIcon className="w-5 h-5" />,
  },
  {
    name: 'Internacional',
    icon: <QrCodeIcon className="w-5 h-5" />,
  },
]

// -- Helper Functions for Version Control --

const cleanStoreCode = (str: string) => {
  if (!str) return ''
  return str.split('-')[0].trim()
}

interface NormalizationContext {
  typeShipment: any[] // Using any[] to avoid complex imports here, but sticking to known structures
  citiesType: any[]
  citiesByBuType: any[]
  businessUnits: BusinessUnitInterface[]
  identificationTypes: any[]
  pieceCategories: any[]
  user: any
  externalManifestInput: string
}

const normalizeShipmentByVersion = (
  shipment: ImportInterface,
  version: 'V1' | 'V2',
  context: NormalizationContext
) => {
  const {
    typeShipment,
    citiesType,
    citiesByBuType,
    businessUnits,
    identificationTypes,
    pieceCategories,
    user,
    externalManifestInput,
  } = context

  shipment.A = shipment.A?.toString()
  shipment.B = shipment.B?.toString()

  shipment.C =
    typeShipment.find(
      (x) =>
        x.value.toString().toUpperCase() ===
          shipment.C?.toString().toUpperCase() ||
        x.name.toString().toUpperCase() === shipment.C?.toString().toUpperCase()
    )?.value ?? ''

  shipment.D =
    citiesType.find(
      (x) =>
        x.locationCode.toString().toUpperCase() ===
          shipment.D?.toString().toUpperCase() ||
        x.locationName.toString().toUpperCase() ===
          shipment.D?.toString().toUpperCase()
    )?.locationCode ?? ''

  let rawStoreCode = shipment.E?.toString().trim()

  if (version === 'V2' && rawStoreCode) {
    rawStoreCode = cleanStoreCode(rawStoreCode)
  }
  const shouldInferStore =
    version === 'V1' || (version === 'V2' && shipment.C === DeliveryEnum.OFFICE)

  const inferredStoreCode =
    shouldInferStore && (!rawStoreCode || rawStoreCode.length === 0)
      ? citiesByBuType.find(
          (x) =>
            x.locationCode ===
            citiesType.find(
              (c) =>
                c.locationCode.toString().toUpperCase() ===
                  shipment.D?.toString().toUpperCase() ||
                c.locationName.toString().toUpperCase() ===
                  shipment.D?.toString().toUpperCase()
            )?.locationCode
        )?.buCode
      : ''

  const finalStoreCodeToSearch = rawStoreCode || inferredStoreCode || ''

  const foundStore = businessUnits.find(
    (x) =>
      x.buCode.toString().toUpperCase() === finalStoreCodeToSearch.toUpperCase()
  )

  shipment.E = foundStore?.buCode ?? ''

  if (version === 'V2' && foundStore?.location.locationCode && !shipment.D) {
    shipment.D =
      foundStore.location.locationCode ??
      citiesType.find(
        (x) =>
          x.locationCode.toString().toUpperCase() ===
            shipment.D?.toString().toUpperCase() ||
          x.locationName.toString().toUpperCase() ===
            shipment.D?.toString().toUpperCase()
      )?.locationCode ??
      ''
  }

  if (version === 'V2' && shipment.C === DeliveryEnum.OFFICE && foundStore) {
    if (
      foundStore.location.address &&
      foundStore.location.address.trim().length > 0
    ) {
      shipment.N = foundStore.location.address
    }
  }

  shipment.F =
    identificationTypes
      .find(
        (x) =>
          (x.abbreviationName === user?.client?.abbreviationName &&
            (shipment.F === undefined ||
              shipment.F?.toString().trim().length === 0)) ||
          x.taxIdentificationTypeId?.toString() ===
            shipment.F?.toString().toUpperCase() ||
          x.abbreviationName?.slice(0, 1) ===
            shipment.F?.toString().toUpperCase() ||
          x.abbreviationName.toUpperCase() ===
            shipment.F?.toString().toUpperCase()
      )
      ?.taxIdentificationTypeId?.toString() ?? ''

  shipment.I =
    identificationTypes
      .find(
        (x) =>
          x.taxIdentificationTypeId.toString() ===
            shipment.I?.toString().toUpperCase() ||
          x.abbreviationName.slice(0, 1) ===
            shipment.I?.toString().toUpperCase() ||
          x.abbreviationName.toUpperCase() ===
            shipment.I?.toString().toUpperCase()
      )
      ?.taxIdentificationTypeId.toString() ?? ''

  if (version === 'V1') {
    shipment.G =
      shipment.G !== undefined &&
      shipment.G !== null &&
      shipment.G?.toString().trim().length > 0
        ? shipment.G?.toString().trim()
        : (user?.client?.identificationNumber?.toString().trim() ?? '')

    shipment.H = shipment.H?.toString().trim()
  } else {
    shipment.G =
      shipment.G !== undefined &&
      shipment.G !== null &&
      shipment.G?.toString().trim().length > 0
        ? shipment.G?.toString().trim()
        : (user?.client?.identificationNumber?.toString().trim() ?? '')

    shipment.H =
      shipment.H !== undefined &&
      shipment.H !== null &&
      shipment.H?.toString().trim().length > 0
        ? shipment.H?.toString().trim()
        : (user?.client?.accountFullName?.toString().trim() ?? '')
  }

  shipment.J = shipment.J?.toString().trim()
  shipment.K = shipment.K?.toString().trim()
  shipment.L = shipment.L?.toString().trim()
  shipment.M = shipment.M?.toString().trim()
  shipment.N = shipment.N?.toString().trim() // Handles V2 updated address + trim
  shipment.O = shipment.O?.toString().trim()
  shipment.P = shipment.P?.toString().trim()
  shipment.Q = shipment.Q?.toString().trim()
  shipment.R = shipment.R?.toString()?.replace(',', '.').trim()
  shipment.S = shipment.S?.toString().trim()
  shipment.T = shipment.T?.toString().trim()
  shipment.U = shipment.U?.toString().trim()
  shipment.V = shipment.V?.toString().trim()

  // 9. Piece Category (W, X)
  // V1: W is category
  // V2: W is Name, X is Code.
  if (version === 'V2') {
    const pName = shipment.W?.toString().trim()
    const pCode = shipment.X?.toString().trim()
    let catId = ''

    if (pCode) {
      catId = pieceCategories.find((c) => c.code === pCode)?.id
    }
    if (!catId && pName) {
      catId = pieceCategories.find((c) => c.name === pName)?.id
    }
    // Default to '49' if no category found or provided in V2
    shipment.W = catId || '49'
  } else {
    // V1 behavior
    shipment.W =
      pieceCategories.find((x) => x.id === shipment.W?.toString().trim())?.id ??
      ''
  }

  shipment.externalManifiest = externalManifestInput
}

const ShipmentImport: FC = () => {
  const user = useAppSelector((state) => state.user)

  // Estado para controlar si la pestaña "Internacional" debe mostrarse
  const [showInternationalTab, setShowInternationalTab] = useState(false)

  // Efecto para actualizar el array de pestañas según la condición
  useEffect(() => {
    if (
      user?.client?.accountCode === '2500' ||
      user?.client?.accountCode === '2629'
    ) {
      setShowInternationalTab(true) // Mostrar la pestaña "Internacional"
    } else {
      setShowInternationalTab(false) // Ocultar la pestaña "Internacional"
    }
  }, [user])

  // Filtramos el array de pestañas según si debe mostrarse la pestaña "Internacional"
  const filteredTabs = CONSIGNEE_TABS.filter(
    (tab) => tab.name !== 'Internacional' || showInternationalTab
  )

  const businessUnits = useAppSelector((state) => state.inmutable.businessUnits)
  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )

  const citiesType = useAppSelector((state) => state.inmutable.cities)
  const citiesByBuType = useAppSelector((state) => state.inmutable.citiesByBu)
  const typeShipment = [
    { value: DeliveryEnum.OFFICE, name: 'Oficina' },
    { value: DeliveryEnum.DELIVERY, name: 'Domicilio' },
  ]

  const [tab, setTab] = useState(0)
  const [error, setError] = useState('')
  const [file, setFile] = useState<File>()
  const [manifestCode, setManifestCode] = useState('')
  const [fileData, setFileData] = useState<ImportInterface[]>([])
  const [nextSave, setNextSave] = useState(false)
  const [guideDocumentCode, setGuideDocumentCode] = useState('')
  const [openManifestModal, setOpenManifestModal] = useState(false)
  const [externalManifestInput, setExternalManifestInput] = useState('')

  let [loading, setLoading] = useState(false)
  let [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING
  )
  let [loadingTitle, setLoadingTitle] = useState('Cargando...')
  const pieceCategories = useAppSelector(
    (state) => state.inmutable.PieceCategories
  )

  const businessUnitOptions = useMemo(() => {
    const business = businessUnits.filter(
      (businessUnit) =>
        //businessUnit.code !== '8110' &&
        businessUnit.buCode !== 'KK' &&
        businessUnit.buCode !== 'NN' &&
        //businessUnit.code !== 'MAD' &&
        //businessUnit.code !== 'MIA' &&
        //businessUnit.code !== 'PTY' &&
        !businessUnit.isLocked &&
        (businessUnit.buTypeID === BusinessType.EXTERNAL ||
          businessUnit.buTypeID === BusinessType.INTERNAL)
    )
    // Delete duplicates by id
    const uniqueIds = new Set()
    const result: BusinessUnitInterface[] = []
    business.forEach((businessUnit, index) => {
      if (!uniqueIds.has(businessUnit.buid)) {
        uniqueIds.add(businessUnit.buid)
        result.push(businessUnit)
      }
    })

    result.sort((a, b) => {
      return 0
    })

    return result
  }, [businessUnits, user.businessUnit])

  const handleFileChange = (file?: File) => {
    if (!file) {
      setFile(undefined)
      return
    }

    const fileType = file.type
    if (
      fileType === 'text/csv' ||
      fileType === 'application/vnd.ms-excel' ||
      fileType ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFile(file)
    } else {
      setError('El archivo no es csv, xls o xlsx')
    }
  }

  const splitString = (str: string) => {
    let arr = str.split(',')
    let result = []
    let temp = ''
    let count = 0
    for (let i = 0; i < arr.length; i++) {
      temp += arr[i]
      count += arr[i].length
      if (count >= 200) {
        result.push(temp)
        temp = ''
        count = 0
      } else {
        temp += ','
        count++
      }
    }
    if (temp) {
      result.push(temp)
    }
    return result
  }
  const handleFileSubmit = async () => {
    if (!file) {
      setError('Por favor seleccione un archivo')
      return
    }

    const paymentMethod = user.paymentMethod?.find(
      (x) => x.paymentModeID.toString() === '3'
    )

    const accountBillTo = paymentMethod?.accountBillTo

    const hasInvalidBillTo =
      paymentMethod &&
      (accountBillTo === '00000000-0000-0000-0000-000000000000' ||
        !accountBillTo)

    if (hasInvalidBillTo) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(
        'Comuniquese con el departamento de Soporte Técnico. No tiene cuenta para facturar asignada'
      )
      setLoading(true)
      return
    }

    if (user.client?.businessUnit! === undefined) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(
        'Comuniquese con el departamento de Soporte Técnico. No tiene tienda origen asignado.'
      )
      setLoading(true)
      return
    }

    setExternalManifestInput('')
    setOpenManifestModal(true)
  }

  const detectFormatVersion = (headers: any): 'V1' | 'V2' => {
    const values = Object.values(headers || {}).map((v) =>
      v?.toString().trim().toUpperCase()
    )

    if (
      values.includes('NOMBRE PRODUCTO') &&
      values.includes('CÓDIGO PRODUCTO')
    ) {
      return 'V2'
    }

    return 'V1'
  }

  const handleConfirmManifest = async () => {
    setOpenManifestModal(false)
    setError('')
    setLoadingStatus(ConfirmModalStatus.PROCESSING)
    setLoadingTitle('Cargando...')
    setLoading(true)

    if (file && file.type === 'text/csv') {
      importShipmentsFromCSV(file)
    } else if (file) {
      let result = await importShipmentsFromXLSX(file)

      let accountId = user?.client?.id ?? ''
      let accountCode = user?.client?.accountCode ?? ''

      const headers = result[0]
      const version = detectFormatVersion(headers)
      result.shift()

      // Filter out empty rows where key fields are missing
      // Especially for V2 which seems to be picking up empty rows
      if (version === 'V2') {
        const originalLength = result.length
        result = result.filter((row) => {
          const hasData =
            (row.A && row.A.toString().trim().length > 0) ||
            (row.B && row.B.toString().trim().length > 0) ||
            (row.X && row.X.toString().trim().length > 0) || // Code
            (row.W && row.W.toString().trim().length > 0) // Name
          return hasData
        })
      }

      // if (result.length < 2) {
      //   setLoadingStatus(ConfirmModalStatus.ERROR)
      //   setLoadingTitle(
      //     'El archivo debe contener al menos 2 registros válidos para ser procesado.'
      //   )
      //   setLoading(true)
      //   return
      // }

      const columnB = result.map(function (value, index) {
        return value['B']
      })
      const concatenatedColumnB = columnB.join(',')
      let dataExistArray = splitString(concatenatedColumnB)
      let dataExits = ''
      for (let i = 0; i < dataExistArray.length; i++) {
        dataExits += await getShipmentsExists(
          dataExistArray[i],
          accountCode,
          accountCode == '' ? accountId : undefined
        )
      }
      setGuideDocumentCode(dataExits)

      result.forEach((shipment) => {
        normalizeShipmentByVersion(shipment, version, {
          typeShipment,
          citiesType,
          citiesByBuType,
          businessUnits,
          identificationTypes,
          pieceCategories,
          user,
          externalManifestInput,
        })
      })
      setFileData(result)
      setNextSave(true)
      setLoadingStatus(ConfirmModalStatus.SUCCESS)
      setLoading(false)
    }
  }

  const SearchManifiest = async () => {
    if (!manifestCode) {
      setError('Por favor ingrese un codigo de manifiesto')
      return
    }
    setError('')
    setLoadingStatus(ConfirmModalStatus.PROCESSING)
    setLoadingTitle('Cargando...')
    setLoading(true)

    const customercode = '' + user?.client?.accountCode

    var data = await searchManifiestInternational(manifestCode, customercode)
    if (data.didError) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle('Error: ' + data?.errorMessage)
      setLoading(true)
      return
    }
    setLoading(false)
    if ((data.model?.length ?? 0) > 0) {
      var datamap =
        data.model?.map((dto: CCWIntegracionV2) => ImportMapperV2Dto(dto)) ?? []
      let numberguide = ''
      datamap?.forEach((shipment) => {
        shipment.C =
          typeShipment.find(
            (x) =>
              x.value.toString().toUpperCase() ===
                shipment.C?.toString().toUpperCase() ||
              x.name.toUpperCase() === shipment.C?.toString().toUpperCase()
          )?.value ?? ''

        shipment.F =
          shipment.F.length > 0
            ? shipment.F
            : (user.client?.abbreviationName ?? '')
        shipment.G =
          shipment.G.length > 0
            ? shipment.G
            : (user.client?.identificationNumber ?? '')
        shipment.H =
          shipment.H.length > 0
            ? shipment.H
            : (user.client?.accountFullName ?? '')

        shipment.D =
          citiesType.find(
            (x) =>
              x.locationCode.toString().toUpperCase() ===
                shipment.D?.toString().toUpperCase() ||
              x.locationName.toUpperCase() ===
                shipment.D?.toString().toUpperCase()
          )?.locationCode ?? ''

        shipment.E =
          businessUnits.find(
            (x) =>
              x.buCode.toString().toUpperCase() ===
              (shipment.E?.toString().length === 0 || shipment.E === undefined
                ? citiesByBuType.find(
                    (x) =>
                      x.locationCode?.toString()?.toUpperCase() ===
                      shipment.D?.toString()?.toUpperCase()
                  )?.buCode
                : shipment.E?.toString())
          )?.buCode ?? ''

        shipment.F =
          identificationTypes
            .find(
              (x) =>
                x.taxIdentificationTypeId.toString() ===
                  shipment.F?.toString().toUpperCase() ||
                x.abbreviationName.slice(0, 1) ===
                  shipment.F?.toString().toUpperCase()
            )
            ?.taxIdentificationTypeId.toString() ?? ''

        shipment.I =
          identificationTypes
            .find(
              (x) =>
                x.taxIdentificationTypeId.toString() ===
                  shipment.I?.toString().toUpperCase() ||
                x.abbreviationName.slice(0, 1) ===
                  shipment.I?.toString().toUpperCase() ||
                x.abbreviationName.toUpperCase() ===
                  shipment.I?.toString().toUpperCase()
            )
            ?.taxIdentificationTypeId.toString() ?? ''

        shipment.G = shipment.G?.toString().trim()
        shipment.H = shipment.H?.toString().trim()
        shipment.J = shipment.J?.toString().trim()
        shipment.K = shipment.K?.toString().trim()
        shipment.L = shipment.L?.toString().trim()
        shipment.M = shipment.M?.toString().trim()
        shipment.N = shipment.N?.toString().trim()
        shipment.O = shipment.O?.toString().trim()
        shipment.P = shipment.P?.toString().trim()
        shipment.Q = shipment.Q?.toString().trim()
        shipment.R = shipment.R?.toString().trim()

        shipment.S = shipment.S?.toString().trim()
        shipment.T = shipment.T?.toString().trim()
        shipment.U = shipment.U?.toString().trim()
        shipment.V = shipment.V?.toString().trim()
        shipment.W =
          pieceCategories.find((x) => x.id === shipment.W?.toString().trim())
            ?.id ?? ''
        shipment.L = shipment.L?.replace(/^(\+58|58)/, '')
        shipment.L = shipment.L?.replace('-', '').replace('.', '')
        shipment.L = shipment.L?.replace(/^(0)?(412|422|414|424|416|426)/, '$2')
        shipment.externalManifiest = manifestCode

        shipment.disabled = numberguide === shipment.B ? true : false
        numberguide = shipment.B
      })
      setFileData(datamap)
      setNextSave(true)
    }
  }

  const DownloadPdf = async () => {
    if (!manifestCode) {
      setError('Por favor ingrese un codigo de manifiesto')
      return
    }
    setError('')
    setLoadingStatus(ConfirmModalStatus.PROCESSING)
    setLoadingTitle('Cargando...')
    setLoading(true)

    const customercode = '' + user?.client?.accountCode

    searchManifiestInternationalExcel(manifestCode, customercode)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`)
        }
        return response.blob()
      })
      .then((blob) => {
        const href = window.URL.createObjectURL(blob)

        const anchorElement = document.createElement('a')

        anchorElement.href = href
        anchorElement.download = 'Importacion' + manifestCode + '.xlsx'

        document.body.appendChild(anchorElement)
        anchorElement.click()

        document.body.removeChild(anchorElement)
        window.URL.revokeObjectURL(href)
        setLoading(false)
      })
      .catch((error) => {
        setLoadingStatus(ConfirmModalStatus.ERROR)
        setLoadingTitle(
          'Error: intente mas tarde, si persiste contacte a soporte tecnico  '
        )
        setLoading(true)
      })
  }

  useEffect(() => {
    if (!nextSave) {
      setError('')
      setFile(undefined)
      setFileData([])
    }
  }, [nextSave])

  return (
    <MainPage>
      <PageTitle title="Cargar guías" />

      {!nextSave && (
        <>
          <HorizontalTab current={tab} tabs={filteredTabs} onChange={setTab} />
          <div className="flex flex-1 flex-col items-center justify-center mt-6 gap-4">
            {tab === 0 && (
              <>
                <div className="w-full flex justify-end items-end">
                  <PrimaryButton
                    onClick={() => {
                      const hasCredit = !!user.paymentMethod?.find(
                        (x) => x.paymentModeID.toString() === '3'
                      )
                      exportFormatoShipmentXLSX_V2(
                        identificationTypes,
                        typeShipment,
                        businessUnitOptions,
                        citiesType,
                        pieceCategories,
                        !hasCredit
                      )
                    }}
                    className="w-full sm:w-40"
                  >
                    Descargar Formato
                    <DocumentArrowDownIcon className="w-5 h-5"></DocumentArrowDownIcon>
                  </PrimaryButton>
                </div>
                <div className="w-full" style={{ maxWidth: '35rem' }}>
                  <FormFile
                    selected={file}
                    error={error}
                    label="Por favor seleccione el archivo que contiene las guías a cargar."
                    description="Archivos admitidos: CSV | XLS | XLSX"
                    id="shipment-import-file"
                    name="shipment-import-file"
                    onSelectFile={handleFileChange}
                  />
                </div>

                <PrimaryButton
                  onClick={handleFileSubmit}
                  className="w-full sm:w-32"
                >
                  Cargar
                </PrimaryButton>
              </>
            )}

            {tab === 1 && (
              <>
                <span className="text-gray-700 text-center mb-8">
                  Por favor ingrese el código de manifiesto internacional.
                </span>

                <div className="w-full" style={{ maxWidth: '35rem' }}>
                  <FormText
                    id="manifest-code"
                    name="manifest-code"
                    label="Código único"
                    value={manifestCode}
                    error={error}
                    onChange={(e) => setManifestCode(e.target.value)}
                    containerClassname="w-full"
                    labelContainerClassname="mb-2"
                  />
                </div>
                <div className="w-full flex items-center justify-center">
                  <PrimaryButton className=" sm:w-32" onClick={SearchManifiest}>
                    Cargar
                  </PrimaryButton>
                  <PrimaryButton
                    className=" sm:w-32 ml-2 flex items-center"
                    onClick={DownloadPdf}
                  >
                    Descargar Excel
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {nextSave && (
        <>
          <div
            className="flex flex-1 justify-between gap-12 md:gap-12 flex-col-reverse md:flex-row mt-2 "
            style={{ maxWidth: '100rem' }}
          >
            <LinkTextIcon
              className="text-sm font-medium cursor-pointer"
              onClick={() => setNextSave(false)}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden="true" />{' '}
              <label>Regresar</label>
            </LinkTextIcon>
          </div>
          <ImportsMasiveTable
            imports={fileData}
            setNext={setNextSave}
            guideExits={guideDocumentCode}
            setGuideExits={setGuideDocumentCode}
          />
        </>
      )}

      <Modal
        open={openManifestModal}
        setOpen={setOpenManifestModal}
        className="w-full max-w-lg"
      >
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-medium text-gray-900">
            Ingresar Nº Manifiesto Externo
          </h2>
          <p className="text-sm text-gray-500">
            Esta acción es opcional, si desea puede ingresar el número de
            manifiesto externo / número externo
          </p>
          <FormText
            id="external-manifest"
            name="external-manifest"
            label="Nº Manifiesto Externo / Nº identificación externo"
            value={externalManifestInput}
            onChange={(e) => setExternalManifestInput(e.target.value)}
            containerClassname="w-full"
            labelContainerClassname="mb-0"
          />
          <div className="flex justify-between gap-2 mt-2">
            <SecondaryButton
              onClick={() => setOpenManifestModal(false)}
              className="w-24"
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton onClick={handleConfirmManifest} className="w-24">
              Siguiente
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <ModalLoading
        open={loading}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setLoading}
        onPrint={() => {}}
      />
    </MainPage>
  )
}

export default ShipmentImport
